import React, { useState } from 'react';
import api from '../../services/api';

/**
 * Modale d'envoi d'email groupé (Resend) à plusieurs contacts sélectionnés.
 * Personnalisation par variables : {civilité} {prenom} {nom} {nom_complet} {societe}.
 * Affiche un résumé « X envoyés / Y échecs / Z sans email » après envoi.
 */
const VARIABLES = [
  { token: '{civilité}', label: 'Civilité' },
  { token: '{prenom}', label: 'Prénom' },
  { token: '{nom}', label: 'Nom' },
  { token: '{nom_complet}', label: 'Nom complet' },
  { token: '{societe}', label: 'Société' },
];

export const ContactBulkEmailModal = ({ open, contactIds = [], onClose, onSent }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Bonjour {civilité} {nom},\n\n');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const insertVariable = (token) => setBody((b) => `${b}${token}`);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      alert('Veuillez renseigner l’objet et le message.');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/contacts/bulk-email', {
        contact_ids: contactIds,
        subject: subject.trim(),
        body,
      });
      setResult(data);
      if (onSent) onSent(data);
    } catch (e) {
      console.error('Erreur envoi groupé:', e);
      const msg = e.response?.status === 503 ? 'Service email non configuré.' : 'Erreur lors de l’envoi groupé.';
      alert(msg);
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setResult(null);
    setSubject('');
    setBody('Bonjour {civilité} {nom},\n\n');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={close} data-testid="bulk-email-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="bulk-email-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            <i className="bi bi-envelope-fill text-blue-600"></i>
            Email groupé — {contactIds.length} contact(s)
          </h3>
          <button onClick={close} className="text-slate-400 hover:text-slate-600" data-testid="bulk-email-close"><i className="bi bi-x-lg"></i></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4" data-testid="bulk-email-result">
            <div className="flex items-center gap-3 text-emerald-700">
              <i className="bi bi-check-circle-fill text-2xl"></i>
              <div>
                <p className="font-bold text-lg">{result.sent} email(s) envoyé(s)</p>
                {result.failed > 0 && <p className="text-sm text-red-600">{result.failed} échec(s)</p>}
              </div>
            </div>
            {result.skipped_no_email?.length > 0 && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-semibold mb-1">{result.skipped_no_email.length} contact(s) sans email (ignorés) :</p>
                <p className="text-xs">{result.skipped_no_email.join(', ')}</p>
              </div>
            )}
            {result.failures?.length > 0 && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                {result.failures.map((f, i) => <p key={i} className="text-xs">{f.contact} : {f.error}</p>)}
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={close} className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold" data-testid="bulk-email-done">Terminé</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Objet</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Objet de l'email…"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  data-testid="bulk-email-subject"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Message</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {VARIABLES.map((v) => (
                    <button
                      key={v.token}
                      type="button"
                      onClick={() => insertVariable(v.token)}
                      className="px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full hover:bg-blue-100"
                      data-testid={`bulk-email-var-${v.label}`}
                    >
                      + {v.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                  data-testid="bulk-email-body"
                />
                <p className="text-[11px] text-slate-400 mt-1">Les variables sont remplacées pour chaque destinataire (ex. {'{nom}'} → nom du contact).</p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={close} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-semibold" data-testid="bulk-email-cancel">Annuler</button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
                data-testid="bulk-email-send"
              >
                {sending ? <i className="bi bi-hourglass-split animate-spin"></i> : <i className="bi bi-send"></i>}
                {sending ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactBulkEmailModal;
